/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_key, t_rec_arr, t_partition } from '../types';
import fold from './fold';
import mimic from '../fn/mimic';

type t_partition_fn<V> = (v: V, k: t_key, adt: t_rec_arr<V>) => boolean;

/**
 * Creates an array of elements split into two groups, the first of which contains elements `predicate` returns truthy for,
 * the second of which contains elements `predicate` returns falsey for. The predicate is invoked with one argument: `(value)`.
 *
 * @example
 *
 * const [even, odd] = partition(n => n % 2 === 0, [1, 2, 3, 4]); // => even: [2, 4], odd: [1, 3]
 */

export interface Partition {
  /**
   * Splits a list by a boolean predicate and places `true` in the first list and `false` in the second list
   *
   * @public
   * @function f1.partition
   * @param adt the array or record to partition in two sets
   * @example
   *
   * ```ts
   * f1.partition(f1.is.odd, f1.identity, f1.identity, [16, 17, 18]) === [[17], [16, 18]]
   * ```
   *
   */
  <V>(fn: t_partition_fn<V>, adt: t_rec_arr<V>): t_partition<V>;
  <V>(fn: t_partition_fn<V>): (adt: t_rec_arr<V>) => t_partition<V>;
}

/**
 * @see Partition
 */
export default <V>(fn: t_partition_fn<V>, adt: t_rec_arr<V>): t_partition<V> => {
  return fold<V, t_partition<V>, t_partition<V>>(
    (acc, v, k, adt) => {
      const index = fn(v, k, adt) ? 0 : 1;

      acc[index][k] = v;
      return acc;
    },
    [mimic(adt), mimic(adt)],
    adt
  );
};
